import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Info, Leaf } from 'lucide-react';
import {
  buildCarbonClaimCopy,
  CARBON_EMBODIED_EXPLAINER,
} from '@siliconx/shared';
import { api, throwIfNotOk } from '../../lib/api';
import { qk } from '../../lib/queryKeys';

/**
 * SIL-191 — avoided-carbon claim for an exchange listing.
 *
 * Renders the legal-approved claim copy (verbatim, built by
 * buildCarbonClaimCopy — never hand-edit the wording here) with an inline
 * info tooltip at the point of claim. The tooltip lazily fetches the SIL-189
 * carbon-footprint detail endpoint for the min-max bounds (the listing
 * payload deliberately carries only thin scalars).
 *
 * The tooltip's "Methodology" link was removed once: it pointed at the
 * internal Cloudflare Pages wiki, which is behind Cloudflare Access and
 * 404'd for every customer. SIL-352 restored it against the in-app
 * `/carbon-methodology` route (a login-gated customer-portal page, not the
 * old externally-gated wiki) — an in-app `<Link>` rather than an `<a>`,
 * since the viewer is already an authenticated customer-portal user.
 *
 * Suppression is server-side: when the listing payload's carbon scalars are
 * null (low completeness / core mass driver defaulted / no snapshot) the
 * parent simply has nothing to pass and this component renders nothing.
 */

const kg = (n: number) =>
  `${Math.round(n).toLocaleString('en-US')} kg CO2eq`;

export function CarbonClaim({
  skuId,
  upToKgco2eq,
  methodologyVersion,
}: {
  skuId: string | null;
  upToKgco2eq: number | null;
  methodologyVersion: string | null;
}) {
  const [open, setOpen] = useState(false);

  // Bounds are fetched lazily, only once the tooltip is opened.
  const { data: detail } = useQuery({
    queryKey: qk.skuCarbonFootprint(skuId ?? ''),
    queryFn: async () => {
      const res = await api.api.client.skus[':skuId']['carbon-footprint'].$get({
        param: { skuId: skuId! },
      });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      return res.json();
    },
    enabled: open && skuId != null,
    staleTime: 5 * 60 * 1000,
  });

  // methodologyVersion gates presence only (null = claim suppressed
  // server-side); the raw stored string is never rendered.
  if (upToKgco2eq == null || methodologyVersion == null) return null;

  const bounds = detail?.carbonFootprint ?? null;

  return (
    <div className="rounded-lg border border-green-200 bg-green-50 p-4">
      <div className="flex items-start gap-3">
        <Leaf className="mt-0.5 size-4 shrink-0 text-green-700" aria-hidden />
        <div className="text-sm text-green-900">
          <span>{buildCarbonClaimCopy(upToKgco2eq)}</span>
          <span className="relative ml-1.5 inline-flex align-middle">
            <button
              type="button"
              aria-label="About this estimate"
              aria-expanded={open}
              onClick={() => setOpen((o) => !o)}
              onBlur={() => setOpen(false)}
              className="text-green-700 hover:text-green-900"
            >
              <Info className="size-4" />
            </button>
            {open && (
              <span
                role="tooltip"
                className="absolute bottom-full left-1/2 z-20 mb-2 w-64 -translate-x-1/2 rounded-md border border-zinc-200 bg-white p-3 text-left text-xs text-zinc-700 shadow-lg"
              >
                <span className="block">{CARBON_EMBODIED_EXPLAINER}</span>
                <span className="mt-2 block font-medium text-zinc-900">
                  Estimate range
                </span>
                <span className="mt-1 block">
                  {bounds
                    ? `${kg(bounds.minKgco2eq)} – ${kg(bounds.maxKgco2eq)}`
                    : skuId
                      ? 'Loading…'
                      : 'Range unavailable.'}
                </span>
                <Link
                  to="/carbon-methodology"
                  className="mt-2 block font-medium text-green-700 underline hover:text-green-900"
                >
                  Methodology
                </Link>
              </span>
            )}
          </span>
        </div>
      </div>
    </div>
  );
}
